/**
 * Vocabulary shared by the client screens.
 *
 * `ClientContact.Relationship` and `ContactMatterAccess.AccessLevel` are both closed
 * sets in `domains/clients/models.py`. They are written out here once so that the "add
 * a contact" form, the "edit a contact" form and the portal-access table cannot drift
 * apart from each other — and so that a value the server does not accept cannot be
 * offered by one of them.
 */

import type { MatterAccessLevel } from '@legalcrm/api';

export const RELATIONSHIP_OPTIONS = [
  { value: 'self', label: 'The client themselves' },
  { value: 'spouse', label: 'Spouse' },
  { value: 'director', label: 'Director' },
  { value: 'representative', label: 'Representative' },
  { value: 'executor', label: 'Executor' },
  { value: 'interpreter', label: 'Interpreter' },
  { value: 'other', label: 'Other' },
];

export function relationshipLabel(value: string): string {
  return RELATIONSHIP_OPTIONS.find((option) => option.value === value)?.label ?? value;
}

/**
 * What a contact may do with a matter in the portal.
 *
 * `none` is not a stored level — the server expresses it as a revocation, so that
 * there is one representation of "no access" rather than two that must agree. It is
 * offered here as the way to take access away, and the wording says so.
 */
export const ACCESS_LEVEL_OPTIONS: Array<{
  value: MatterAccessLevel;
  label: string;
  description: string;
}> = [
  {
    value: 'none',
    label: 'No access',
    description: 'This matter does not appear in their portal at all.',
  },
  {
    value: 'read_only',
    label: 'Read only',
    description: 'They can see the matter and what the firm has published on it.',
  },
  {
    value: 'participate',
    label: 'Participate',
    description: 'They can see the matter and take part — messages, uploads, signatures.',
  },
];

export function accessLevelLabel(value: MatterAccessLevel | string): string {
  return ACCESS_LEVEL_OPTIONS.find((option) => option.value === value)?.label ?? value;
}
